import { useEffect } from "react";
import {
  AppShell,
  Burger,
  Group,
  Menu,
  NavLink as MantineNavLink,
  Select,
  Stack,
  Text,
} from "@mantine/core";
import { useDisclosure } from "@mantine/hooks";
import { IconChevronDown, IconLogout, IconSettings, IconUser } from "@tabler/icons-react";
import { useTranslation } from "react-i18next";
import { Link, Outlet, useLocation, useNavigate } from "react-router";

import { hasAnyView, hasRole, hasView, useAuth } from "@/auth/auth-context";
import { BrandMark } from "@/components/ui/brand-mark";
import { Button } from "@/components/ui/button";
import { useLocale } from "@/i18n/use-locale";
import { ROLE_LABELS } from "@/features/users/user-labels";
import { NAV_ITEMS, type NavItem } from "./nav-items";

/**
 * Application chrome: header + responsive navbar + main.
 *
 * Mantine's AppShell collapses the navbar under `md` and the Burger toggles
 * it with the correct `aria-expanded` / `aria-controls` wiring. Print pages
 * hide the chrome via `.app-shell-chrome` in `index.css` (ADR-073, ADR-074).
 */
export function AppLayout() {
  const [opened, { toggle, close }] = useDisclosure(false);
  const location = useLocation();
  const { t } = useTranslation("common");

  // Navigating on a phone must close the drawer, otherwise the new page is
  // hidden behind the menu that opened it.
  useEffect(() => {
    close();
  }, [location.pathname, close]);

  // Marketing sets a longer tab title; the console stays on the product name.
  useEffect(() => {
    document.title = "ZonaClara";
  }, []);

  return (
    <>
      <a href="#main" className="skip-link">
        {t("skipToContent")}
      </a>

      <AppShell
        header={{ height: 60 }}
        navbar={{
          width: 240,
          breakpoint: "md",
          collapsed: { mobile: !opened },
        }}
        padding="md"
      >
        <AppShell.Header className="app-shell-chrome" withBorder>
          <Group h="100%" px="md" justify="space-between" wrap="nowrap">
            <Group gap="sm" wrap="nowrap">
              <Burger
                opened={opened}
                onClick={toggle}
                hiddenFrom="md"
                size="sm"
                aria-label={opened ? t("closeMenu") : t("openMenu")}
              />
              <BrandMark size="sm" />
            </Group>

            <TopBarAccount />
          </Group>
        </AppShell.Header>

        <AppShell.Navbar className="app-shell-chrome" p="sm" withBorder>
          <AppShell.Section
            grow
            component="nav"
            aria-label={t("mainNavigation")}
            id="primary-navigation"
          >
            <Stack gap={4}>
              <NavEntries pathname={location.pathname} onNavigate={close} />
            </Stack>
          </AppShell.Section>
        </AppShell.Navbar>

        <AppShell.Main id="main">
          <div className="mx-auto w-full max-w-7xl print:max-w-none">
            <Outlet />
          </div>
        </AppShell.Main>
      </AppShell>
    </>
  );
}

function isNavItemVisible(user: ReturnType<typeof useAuth>["user"], item: NavItem): boolean {
  if (!hasRole(user, item.allowed)) {
    return false;
  }

  if (item.anyOfViews) {
    return hasAnyView(user, item.anyOfViews);
  }

  return hasView(user, item.view);
}

function isNavItemActive(pathname: string, item: NavItem): boolean {
  if (pathname === item.to || (item.to !== "/" && pathname.startsWith(`${item.to}/`))) {
    return true;
  }

  return (item.alsoActiveFor ?? []).some(
    (prefix) => pathname === prefix || pathname.startsWith(`${prefix}/`),
  );
}

function NavEntries({ pathname, onNavigate }: { pathname: string; onNavigate: () => void }) {
  const { user } = useAuth();
  const { t } = useTranslation("nav");

  return (
    <>
      {NAV_ITEMS.filter((item) => isNavItemVisible(user, item)).map((item) => {
        const active = isNavItemActive(pathname, item);
        const Icon = item.icon;

        return (
          <MantineNavLink
            key={item.to}
            component={Link}
            to={item.to}
            label={t(item.labelKey)}
            leftSection={<Icon size={20} stroke={1.75} aria-hidden />}
            active={active}
            onClick={onNavigate}
            // `aria-current` is set by Mantine when `active` is true, so a
            // screen reader hears the current section and not only sees colour.
            color="brand"
            variant="light"
          />
        );
      })}
    </>
  );
}

function TopBarAccount() {
  const { user, logout } = useAuth();
  const { t } = useTranslation("common");
  const { locale, setLocale, locales, labels, showSwitcher } = useLocale();

  return (
    <Group gap="sm" wrap="nowrap">
      {showSwitcher ? (
        <Select
          aria-label={t("language")}
          data={locales.map((value) => ({ value, label: labels[value] }))}
          value={locale}
          onChange={(value) => {
            if (value === "en-GB" || value === "es-AR") {
              void setLocale(value);
            }
          }}
          allowDeselect={false}
          w={118}
          size="xs"
          comboboxProps={{ withinPortal: true }}
        />
      ) : null}

      {user ? (
        <div className="hidden text-right leading-tight sm:block">
          {/* The active organization is always visible: a super admin
              working across tenants has to know where they are. */}
          <Text size="sm" fw={500}>
            {user.organizationName ?? t("allOrganizations")}
          </Text>
          <Text size="xs" c="dimmed">
            {ROLE_LABELS[user.role]}
          </Text>
        </div>
      ) : null}

      {user ? (
        <AccountMenu />
      ) : (
        <Button variant="secondary" size="sm" onClick={() => void logout()}>
          <IconLogout size={18} stroke={1.75} aria-hidden />
          {t("signOut")}
        </Button>
      )}
    </Group>
  );
}

function AccountMenu() {
  const { user, logout } = useAuth();
  const { t } = useTranslation("common");
  const navigate = useNavigate();

  if (!user) {
    return null;
  }

  return (
    <Menu
      position="bottom-end"
      shadow="md"
      width={240}
      withinPortal={false}
      transitionProps={{ duration: 0 }}
    >
      <Menu.Target>
        <button
          type="button"
          className="flex max-w-[14rem] items-center gap-2 rounded-md border border-border-subtle bg-surface px-3 py-1.5 text-left text-sm text-ink"
          aria-label={t("accountMenu")}
        >
          <span className="min-w-0">
            <span className="block truncate font-medium">{user.name}</span>
            <span className="block truncate text-xs text-ink-muted sm:hidden">
              {ROLE_LABELS[user.role]}
            </span>
          </span>
          <IconChevronDown size={16} stroke={1.75} aria-hidden />
        </button>
      </Menu.Target>

      <Menu.Dropdown>
        <Menu.Item
          leftSection={<IconUser size={16} stroke={1.75} aria-hidden />}
          onClick={() => {
            void navigate("/profile");
          }}
        >
          {t("profile")}
        </Menu.Item>
        {hasRole(user, ["SUPER_ADMIN"]) ? (
          <Menu.Item
            leftSection={<IconSettings size={16} stroke={1.75} aria-hidden />}
            onClick={() => {
              void navigate("/platform-settings");
            }}
          >
            {t("platformSettings")}
          </Menu.Item>
        ) : null}
        <Menu.Divider />
        <Menu.Item
          leftSection={<IconLogout size={16} stroke={1.75} aria-hidden />}
          onClick={() => void logout()}
        >
          {t("signOut")}
        </Menu.Item>
      </Menu.Dropdown>
    </Menu>
  );
}
